import {Controller} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {AddressAutocompleteField} from './AddressAutocompleteField';

import type {AddressAutocompleteFieldProps} from './AddressAutocompleteField';
import type {Control, FieldPath, FieldValues} from 'react-hook-form';

/** `onAiChange` fires after the address commits — e.g. to clear an AI "filled" indicator on manual edit. */
export interface ControlledAddressFieldProps<TFieldValues extends FieldValues> extends Omit<
  AddressAutocompleteFieldProps,
  'value' | 'onChange'
> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  onAiChange?: () => void;
  validateRequired?: boolean;
}

/** Owns the Controller/rules contract for an address field, defaulting to required like every other address field in the app — pass `required={false}` for the rare optional one (e.g. an asset descriptor that allows a parcel instead). `validateRequired` decouples the asterisk from RHF's own block, for draft-tolerant forms that only soft-validate via a schema (mirrors `TaxResidencyFields`). */
export function ControlledAddressField<TFieldValues extends FieldValues>({
  control,
  name,
  onAiChange,
  required = true,
  validateRequired = required,
  ...props
}: ControlledAddressFieldProps<TFieldValues>) {
  const {t} = useTranslation('common');

  return (
    <Controller
      name={name}
      control={control}
      rules={{required: validateRequired && t('fields.address.required')}}
      render={({field}) => (
        <AddressAutocompleteField
          {...props}
          required={required}
          value={field.value as string}
          onChange={address => {
            field.onChange(address);
            onAiChange?.();
          }}
          onBlur={field.onBlur}
          ref={field.ref}
        />
      )}
    />
  );
}
